import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import styled from "styled-components";
import { Tooltip } from "@monolith-forensics/monolith-ui";

interface TruncatedTextCellProps {
  label: string;
  value?: string | number | null;
}

const TriggerSpan = styled.span`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: 1px;
  }
`;

const CopyableContent = styled.div`
  position: relative;
  min-width: 240px;
  max-width: 420px;
  padding: 6px 24px 6px 8px;
  font-size: 0.75rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.palette.text.primary};
  white-space: pre-wrap;
  word-break: break-word;
  border-radius: 3px;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.palette.action.hover};
    .copy-icon {
      visibility: visible;
    }
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: 1px;
  }

  .copy-icon {
    visibility: hidden;
    position: absolute;
    top: 6px;
    right: 6px;
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.palette.text.secondary};

    &[data-copied="true"] {
      visibility: visible;
      color: ${({ theme }) => theme.palette.success.main};
    }
  }
`;

const TruncatedTextCell: React.FC<TruncatedTextCellProps> = ({
  label,
  value,
}) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  if (value === undefined || value === null || value === "") {
    return null;
  }

  const toCopy = String(value);

  const handleCopy = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(toCopy)
      .then(() => {
        setCopied(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCopy(e);
    }
  };

  const tooltipContent = (
    <CopyableContent
      onClick={handleCopy}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Copy ${label}: ${toCopy}`}
    >
      {toCopy}
      <div className="copy-icon" data-copied={copied}>
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </div>
    </CopyableContent>
  );

  return (
    <Tooltip
      content={tooltipContent}
      hoverableContent
      delayDuration={400}
      side="bottom"
      align="start"
      sideOffset={5}
    >
      <TriggerSpan tabIndex={0} aria-label={`${label}: ${toCopy}`}>
        {value}
      </TriggerSpan>
    </Tooltip>
  );
};

export default TruncatedTextCell;
